/**
 * Public Passport client (F7.7), no login: uc-core GET /passport/10/{lot} or
 * /passport/01/{gtin}/10/{lot} — the pouch link's own path, so the server checks the GTIN and logs
 * the scan as it was printed. Status and hold are read live by the server on every request
 * (Cache-Control max-age=60); the page keeps no copy.
 *
 * 404 → not found (unknown code, wrong check letter, or a GTIN that does not match the lot).
 * In development builds a code from the demo data is answered on the phone when the server has
 * no such lot, so the pages can be seen without a backend.
 */
import { api, isApiError } from '@/api'
import { env } from '@/lib/env'
import { passportResponseSchema, toView, type PassportView } from './model'

/** The resolver path for a lot, as printed on the pouch. */
export function passportPath(code: string, gtin?: string | null): string {
  const lot = encodeURIComponent(code)
  return gtin ? `/passport/01/${encodeURIComponent(gtin)}/10/${lot}` : `/passport/10/${lot}`
}

/** The lab report: the server answers 302 to a signed link to the PDF, valid 5 minutes. */
export function labReportUrl(code: string): string {
  return `${env.apiBaseUrl}/passport/${encodeURIComponent(code)}/lab-report`
}

async function demoView(code: string): Promise<PassportView | null> {
  if (!import.meta.env.DEV) return null
  const { demoPassport } = await import('./demo')
  const res = demoPassport(code)
  return res ? toView(code, passportResponseSchema.parse(res)) : null
}

export async function fetchPassport(code: string, gtin?: string | null, signal?: AbortSignal): Promise<PassportView> {
  try {
    const res = await api.get(passportPath(code, gtin), {
      anonymous: true,
      schema: passportResponseSchema,
      signal,
      timeoutMs: 15_000,
    })
    return toView(code, res)
  } catch (e) {
    const demo = await demoView(code)
    if (demo) return demo
    if (isApiError(e) && e.kind === 'not_found') return { kind: 'NOT_FOUND', code, contact: null }
    throw e
  }
}
